// Copyright 2026 - Hewlett Packard Enterprise Company

import { useCallback, useEffect, useRef, useState } from 'react';
import { ContextualHelp as ShellContextualHelp } from '@greenlake-shell/contextual-help';

export default function ShellContextualHelpShim({
  topicsId,
  onOpen,
  onClose,
  showSingleTopicList,
  ...rest
}) {
  const [panelKey, setPanelKey] = useState(0);
  const latestTopicsId = useRef(topicsId);
  const routeChangedWhileOpen = useRef(false);
  const shouldReopenAfterRemount = useRef(false);

  const syncOpenState = useCallback(
    nextOpen => {
      document.body.classList.toggle('helpOpen', nextOpen);

      if (nextOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    [onClose, onOpen],
  );

  useEffect(() => {
    if (
      latestTopicsId.current !== topicsId &&
      document.body.classList.contains('helpOpen')
    ) {
      routeChangedWhileOpen.current = true;
    }
    latestTopicsId.current = topicsId;
  }, [topicsId]);

  const onContextHelp = useCallback(
    ev => {
      const currentOpen = document.body.classList.contains('helpOpen');

      if (typeof ev?.detail?.open === 'boolean') {
        syncOpenState(ev.detail.open);
        return;
      }

      if (typeof ev?.detail?.topic === 'string' && ev.detail.topic) {
        syncOpenState(true);
        return;
      }

      syncOpenState(!currentOpen);
    },
    [syncOpenState],
  );

  useEffect(() => {
    window.addEventListener('context-help', onContextHelp);
    window.addEventListener('contextHelp', onContextHelp);

    return () => {
      window.removeEventListener('context-help', onContextHelp);
      window.removeEventListener('contextHelp', onContextHelp);
    };
  }, [onContextHelp]);

  const handleClose = useCallback(() => {
    syncOpenState(false);
  }, [syncOpenState]);

  const onPinClickCapture = useCallback(
    ev => {
      const pinButton = ev.target?.closest?.(
        'button[data-track-id="ctx-help-pane-pin"]',
      );
      if (!pinButton || !routeChangedWhileOpen.current) {
        return;
      }

      routeChangedWhileOpen.current = false;
      // Close before remounting so the new instance starts in a clean closed state,
      // avoiding a stuck spinner when body.helpOpen is already true on mount.
      shouldReopenAfterRemount.current = true;
      syncOpenState(false);
      setPanelKey(prev => prev + 1);
    },
    [syncOpenState],
  );

  useEffect(() => {
    document.addEventListener('click', onPinClickCapture, true);

    return () => {
      document.removeEventListener('click', onPinClickCapture, true);
    };
  }, [onPinClickCapture]);

  // Re-open after a remount triggered by unpin-after-navigation so the new
  // instance opens cleanly to the current page's topic index.
  useEffect(() => {
    if (shouldReopenAfterRemount.current) {
      shouldReopenAfterRemount.current = false;
      syncOpenState(true);
    }
    // panelKey in deps ensures this runs once per remount, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelKey]);

  return (
    <ShellContextualHelp
      key={panelKey}
      topicsId={topicsId}
      onOpen={onOpen}
      onClose={handleClose}
      showSingleTopicList={showSingleTopicList}
      {...rest}
    />
  );
}
